import CTNButton from '@ctn/ui/components/ctn-button';
import CTNButtonsGroup from '@ctn/ui/components/ctn-buttons-group';
import CTNMessage from '@ctn/ui/components/ctn-message';
import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect, type ReactElement } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { type SujetoForm } from '@/components/Interviniente/config';
import DatosJuridica from '@/components/Interviniente/form/DatosJuridica';
import DatosPersona from '@/components/Interviniente/form/DatosPersona';
import Direccion from '@/components/Interviniente/form/Direccion';
import TipoInterviniente from '@/components/Interviniente/form/TipoInterviniente';
import ModalSubsanacionInterviniente from '@/components/ModalSubsanacionInterviniente';
import PageHeader from '@/components/PageHeader';
import RepresentanteListado from '@/components/RepresentanteListado';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useLoadTramite from '@/hooks/useLoadTramite';
import useRouteTramite from '@/hooks/useRouteTramite';

import { TIPO_DIRECCION, TIPO_PERSONA } from '@/constants/tramite.constants';
import { mapSujetoDBToForm } from '@/helpers/formSujeto';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';
import { type ISujetoListado, type FormTramiteValues } from '@/interfaces/model/don.interface';

import { ROUTES_LABEL } from '../../constants/routes.constants';

/**
 * [REQUERIDO] Enviar la url parametros idTramite y idSujeto: /solicitudes/:idTramite/documentacion/:idSujeto
 * [OPCIONAL] En el caso de querer subsanar el sujeto se envia mediante params en la url: /solicitudes/:idTramite/sujeto/:idSujeto/:idTipoSubsanacion/:idSubsanacion
 */
const TramiteSujetoDetalle = (): ReactElement => {	
	const { idTramite, idTipoSubsanacion, idSujeto, subsanacion } = useParams();
	const { isShowing, openModal, closeModal } = useModal({});
	const { watch: watchGlobal } = useFormContext<FormTramiteValues>();
	const isSubsanable = Boolean(subsanacion !== '0');

	const navigate = useNavigate();
	const { user, getTramiteData } = useGlobalContext();
	const { hasData } = useLoadTramite(Number(idTramite), true);
	const ROUTES_TRAMITE = useRouteTramite();
	const { appendBreadcrumb } = usePageContext();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });

	const methods = useForm<SujetoForm>({
		mode: 'onChange',
	});

	const { reset, watch } = methods;

	const subsanacionPendiente = watch('subsanacionPendiente');
	const idTipoDireccion = watch('direccion.idTipoDireccion');
	const idTipoPersona = watch('idTipoPersona');

	const getData = async (sujetos: ISujetoListado[]) => {
		if (!idSujeto || !user) {
			openNotification(
				notificationTemplate.errorMessage('No se pudo cargar la información necesaria para el detalle.'),
			);
			return;
		}

		const sujeto = sujetos.find(({ id }) => id === Number(idSujeto));

		if (sujeto) {
			const sujetoMapped = mapSujetoDBToForm(sujeto);
			reset(sujetoMapped);
		}
	};

	const onSubmit = () => {
		navigate(`${ROUTES_TRAMITE.SOLICITUDES}/${idTramite}`);
	};

	useEffect(() => {
		appendBreadcrumb(ROUTES_LABEL.SUJETO_DETALLE, [
			getBreadcrumbTramite(getTramiteData()),
			{ label: ROUTES_LABEL.SOLICITUDES, url: ROUTES_TRAMITE.SOLICITUDES },
			{ label: ROUTES_LABEL.SOLICITUD, url: `${ROUTES_TRAMITE.SOLICITUDES}/${idTramite}` },
			{ label: 'Interviniente', url: location.pathname },
		]);
	}, []);

	useEffect(() => {
		if (hasData) {
			const sujetos = watchGlobal('sujetos');
			getData(sujetos);
		}
	}, [hasData]);

	return (
		<>
			<PageHeader hasHelp title={ROUTES_LABEL.SUJETO_DETALLE} className="u-mt-8" />
			<div className="[ p-detail-parties ] [ c-pnp-box c-pnp-box--border c-pnp-box--border--breakout ] [ u-mt-24 u-flow-border ]">
				<div className="c-pnp-box__body">
					{isSubsanable && (
						<CTNMessage
							content={
								<>
									<p>
										<b>Los datos de este usuario están pendiente de subsanación.</b>
									</p>
									<p>
										Motivos indicados por el notario: {subsanacionPendiente?.observacionesPeticion}.
									</p>
								</>
							}
							className="u-mt-24"
							icon="warning"
							status="warning"
							mode="outline"
							closable={false}
						/>
					)}
					<FormProvider {...methods}>
						<TipoInterviniente isDisplay className={isSubsanable ? 'u-mt-32' : ''} />
						{Number(idTipoPersona) === TIPO_PERSONA.JURIDICA ? (
							<DatosJuridica isDisplay className="u-mt-24" />
						) : (
							<DatosPersona isDisplay className="u-mt-24" />
						)}
						{Number(idTipoDireccion) !== TIPO_DIRECCION.SIN_DIRECCION && (
							<Direccion isDisplay title="Dirección" className="u-mt-56 u-mt-40@portrait" />
						)}
						{Number(idTipoPersona) === TIPO_PERSONA.JURIDICA && (
							<div className="u-mt-40">
								<h4 className="c-pnp-heading-h4">Representantes</h4>
								<RepresentanteListado isDisplay className="u-mt-16" />
							</div>
						)}
					</FormProvider>
				</div>
				<footer className="c-pnp-box__footer">
					<CTNButtonsGroup align="right">
						<CTNButton
							label={isSubsanable ? 'Cancelar' : 'Cerrar'}
							mode="tertiary"
							minWidth="140"
							size="md"
							onClick={() => navigate(`${ROUTES_TRAMITE.SOLICITUDES}/${idTramite}`)}
						/>
						{isSubsanable && (
							<CTNButton label="Subsanar" mode="primary" minWidth="140" size="md" onClick={openModal} />
						)}
					</CTNButtonsGroup>
				</footer>
			</div>
			<ModalSubsanacionInterviniente
				data={
					{
						idTramite: idTramite as any,
						id: (subsanacion ?? null) as any,
						idTipo: (idTipoSubsanacion ?? null) as any,
					} as any
				}
				isOpen={isShowing}
				onClose={closeModal}
				onSubmit={onSubmit}
			/>
		</>
	);
};

export default TramiteSujetoDetalle;
